/**
 * CESM OSINT - Conflict Events Feed (style IranWarMap)
 * Affiche les evenements de conflit en temps reel
 * depuis GDELT et RSS OSINT
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Crosshair, Rocket, Zap, Plane, Anchor, Shield,
  Users, MessageCircle, AlertTriangle, Target, Radio,
  RefreshCw, Loader2, ExternalLink, MapPin, Filter, X, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  airstrike: Crosshair,
  missile: Rocket,
  explosion: Zap,
  drone: Plane,
  naval: Anchor,
  air_defense: Shield,
  mobilization: Users,
  diplomatic: MessageCircle,
  rocket_alert: AlertTriangle,
  ground_operation: Target,
  other: Radio,
};

const PRIORITY_COLORS = {
  high: 'border-l-red-500 bg-red-900/10',
  medium: 'border-l-amber-500 bg-amber-900/5',
  low: 'border-l-slate-500 bg-slate-900/5',
};

function EventCard({ event }) {
  const Icon = EVENT_ICONS[event.event_type] || Radio;
  const priorityClass = PRIORITY_COLORS[event.priority] || PRIORITY_COLORS.low;

  return (
    <div 
      className={`border-l-2 ${priorityClass} border border-white/5 rounded-r p-2.5 hover:bg-white/5 transition-all`}
      data-testid={`conflict-event-${event.event_type}`}
    >
      <div className="flex items-start gap-2">
        <div 
          className="w-6 h-6 rounded flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: event.color + '20', color: event.color }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono px-1 py-0.5 rounded" style={{ 
              backgroundColor: event.color + '20', 
              color: event.color 
            }}>
              {event.event_type?.replace('_', ' ').toUpperCase()}
            </span>
            <span className={`text-[9px] font-mono px-1 py-0.5 rounded ${
              event.priority === 'high' ? 'bg-red-900/40 text-red-400' :
              event.priority === 'medium' ? 'bg-amber-900/40 text-amber-400' :
              'bg-slate-800 text-slate-400'
            }`}>
              {event.priority?.toUpperCase()}
            </span>
          </div>
          <p className="text-xs font-mono text-white/80 mt-1 leading-relaxed line-clamp-2">
            {event.title}
          </p>
          {event.location && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-white/40 font-mono">
              <MapPin className="w-3 h-3" />
              {event.location.name} ({event.location.country})
            </div>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-white/30 font-mono">{event.source} | {event.source_type}</span>
            {event.url && (
              <a 
                href={event.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] text-marine-cyan/70 hover:text-marine-cyan font-mono flex items-center gap-0.5"
              >
                <ExternalLink className="w-3 h-3" /> Source
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConflictEventsFeed({ token, theater = 'ormuz', onEventsLoaded }) {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/map/conflict-events`, {
        params: { theater, limit: 50 },
        ...authHeaders,
      });
      setEvents(res.data.events || []);
      setStats(res.data);
      if (onEventsLoaded) {
        onEventsLoaded(res.data.events?.filter(e => e.location) || []);
      }
    } catch (err) {
      console.error('Conflict events error:', err);
    } finally {
      setLoading(false);
    }
  }, [theater, token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filteredEvents = events.filter(e => {
    if (filterType && e.event_type !== filterType) return false;
    if (filterPriority && e.priority !== filterPriority) return false;
    return true;
  });

  const eventTypes = stats.event_types || {};

  return (
    <div 
      className="bg-black/60 border border-white/10 rounded-lg overflow-hidden"
      data-testid="conflict-events-feed"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-red-900/30 to-transparent border-b border-white/10 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-xs font-mono text-white font-bold">OSINT FEED</span>
          <span className="text-[10px] font-mono text-white/40">
            {events.length} evenements | {stats.located || 0} geolocalises
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); fetchEvents(); }}
            disabled={loading}
            className="text-white/50 hover:text-white"
            data-testid="refresh-conflict-events"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <>
          {/* Quick Filters */}
          <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-white/5">
            <button
              onClick={() => { setFilterType(null); setFilterPriority(null); }}
              className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${!filterType && !filterPriority ? 'bg-marine-cyan/20 text-marine-cyan' : 'text-white/40 hover:text-white/60'}`}
            >
              Tous
            </button>
            <button
              onClick={() => setFilterPriority(filterPriority === 'high' ? null : 'high')}
              className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${filterPriority === 'high' ? 'bg-red-900/40 text-red-400' : 'text-white/40 hover:text-white/60'}`}
            >
              HIGH
            </button>
            {Object.entries(eventTypes).map(([type, count]) => (
              <button
                key={type}
                onClick={() => setFilterType(filterType === type ? null : type)}
                className={`px-1.5 py-0.5 rounded text-[10px] font-mono flex items-center gap-0.5 ${filterType === type ? 'bg-marine-cyan/20 text-marine-cyan' : 'text-white/40 hover:text-white/60'}`}
              >
                {type.replace('_', ' ')} ({count})
              </button>
            ))}
          </div>

          {/* Events List */}
          <ScrollArea className="max-h-[350px]">
            <div className="p-2 space-y-1.5">
              {filteredEvents.map((event, i) => (
                <EventCard key={i} event={event} />
              ))}
              {filteredEvents.length === 0 && (
                <div className="text-center py-6 text-white/30 font-mono text-xs">
                  {loading ? 'Chargement...' : 'Aucun evenement pour ce filtre'}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-white/5 text-[10px] font-mono text-white/30 flex justify-between">
            <span>Sources: GDELT ({stats.sources?.gdelt || 0}) + RSS ({stats.sources?.rss || 0})</span>
            <span>{stats.data_source || 'LIVE_OSINT'}</span>
          </div>
        </>
      )}
    </div>
  );
}
